import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Textarea } from './textarea';

/**
 * Optimized Textarea that uses local state to prevent keystroke lag
 * Syncs to parent state on blur or after a debounce period
 */
export const DebouncedTextarea = React.forwardRef(({ 
  value: externalValue, 
  onChange: onExternalChange,
  debounceMs = 300,
  syncOnBlur = true,
  ...props 
}, ref) => {
  const [localValue, setLocalValue] = useState(externalValue || '');
  const timeoutRef = useRef(null);
  const internalRef = useRef(null);
  const textareaRef = ref || internalRef;

  // Sync from external value when it changes (and textarea is not focused)
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea && document.activeElement !== textarea) {
      setLocalValue(externalValue || '');
    }
  }, [externalValue, textareaRef]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleChange = useCallback((e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Debounced sync to parent
    if (!syncOnBlur) {
      timeoutRef.current = setTimeout(() => {
        if (onExternalChange) {
          onExternalChange({ target: { value: newValue } });
        }
      }, debounceMs);
    }
  }, [onExternalChange, debounceMs, syncOnBlur]);

  const handleBlur = useCallback(() => {
    // Clear any pending debounce
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Sync immediately on blur
    if (localValue !== externalValue && onExternalChange) {
      onExternalChange({ target: { value: localValue } });
    }
  }, [localValue, externalValue, onExternalChange]);

  return (
    <Textarea
      ref={textareaRef}
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      {...props}
    />
  );
});

DebouncedTextarea.displayName = 'DebouncedTextarea';

export default DebouncedTextarea;
